import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, Receipt, AlertTriangle, Wallet, Plug, Activity as ActivityIcon, ScrollText, ShieldAlert, ArrowLeft, Fingerprint, Sparkles, Snowflake, Ban, RefreshCw, Lock, ShieldCheck, BadgeCheck, CreditCard, CheckCircle2, XCircle, UsersRound, KeyRound, Trash2, UserCog, MessageSquare, Send as SendIcon, LifeBuoy, Search, UserCheck, Power, Share2, Mail, Eye, EyeOff, Copy, Check, Plus } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { SplashDesign } from "../components/SplashDesign";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { StatusBadge, FullLoader, VerifiedBadge, Spinner, AuthedImg } from "../components/common";
import AnalyticsTab from "./admin/AnalyticsTab";
import CardDesignTab from "./admin/CardDesignTab";
import Cropper from "react-easy-crop";

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", reject);
    img.src = url;
  });
}

async function getCroppedImg(src, cropPx, removeBg) {
  const image = await createImage(src);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(cropPx.width));
  canvas.height = Math.max(1, Math.round(cropPx.height));
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, cropPx.x, cropPx.y, cropPx.width, cropPx.height, 0, 0, canvas.width, canvas.height);
  if (removeBg) {
    const id = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const d = id.data;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i] > 232 && d[i + 1] > 232 && d[i + 2] > 232) d[i + 3] = 0;
    }
    ctx.putImageData(id, 0, 0);
  }
  return new Promise((resolve) => canvas.toBlob((b) => resolve(new File([b], "email-logo.png", { type: "image/png" })), "image/png"));
}

function CropModal({ open, image, onClose, onDone, busy }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(3 / 1);
  const [removeBg, setRemoveBg] = useState(true);
  const [areaPx, setAreaPx] = useState(null);
  useEffect(() => { if (open) { setCrop({ x: 0, y: 0 }); setZoom(1); setAreaPx(null); } }, [open, image]);
  if (!open) return null;
  const done = async () => {
    if (!areaPx) return;
    const file = await getCroppedImg(image, areaPx, removeBg);
    onDone(file);
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-card rounded-3xl p-5" onClick={(e) => e.stopPropagation()} data-testid="logo-crop-modal">
        <h3 className="font-display text-lg font-semibold mb-1">Crop your logo</h3>
        <p className="text-xs text-muted-foreground mb-3">Drag to reposition, zoom to fit. Turn on transparency to drop a white background.</p>
        <div className="relative w-full rounded-2xl overflow-hidden"
          style={{ height: 280, backgroundColor: "#fff", backgroundImage: "repeating-conic-gradient(#e9e9ef 0% 25%, #ffffff 0% 50%)", backgroundSize: "20px 20px" }}>
          <Cropper image={image} crop={crop} zoom={zoom} aspect={aspect} restrictPosition={false}
            onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={(_, px) => setAreaPx(px)} />
        </div>
        <div className="flex items-center gap-2 mt-4">
          <span className="text-xs text-muted-foreground w-12">Shape</span>
          {[["Wide", 3 / 1], ["Square", 1 / 1], ["Tall", 2 / 3]].map(([l, a]) => (
            <button key={l} data-testid={`crop-aspect-${l.toLowerCase()}`} onClick={() => setAspect(a)}
              className={`px-3 h-8 rounded-lg text-xs font-semibold ${aspect === a ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-3 mt-3">
          <span className="text-xs text-muted-foreground w-12">Zoom</span>
          <input type="range" min={1} max={3} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1" data-testid="crop-zoom" />
        </div>
        <button data-testid="crop-removebg-toggle" onClick={() => setRemoveBg(!removeBg)} className="flex items-center gap-2.5 mt-4 text-sm font-medium">
          <span className={`w-10 h-6 rounded-full transition-colors relative shrink-0 ${removeBg ? "bg-primary" : "bg-secondary"}`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${removeBg ? "left-[18px]" : "left-0.5"}`} />
          </span>
          Remove white background (transparent)
        </button>
        <div className="flex gap-3 mt-5">
          <button data-testid="crop-cancel" onClick={onClose} className="flex-1 h-11 rounded-xl bg-secondary font-semibold">Cancel</button>
          <button data-testid="crop-apply" onClick={done} disabled={busy || !areaPx}
            className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center disabled:opacity-60">
            {busy ? <Spinner className="w-5 h-5" /> : "Use this logo"}
          </button>
        </div>
      </div>
    </div>
  );
}

const TABS = [
  { id: "overview", label: "Dashboard", icon: Wallet, perm: null },
  { id: "analytics", label: "Analytics", icon: ActivityIcon, perm: "settings.manage" },
  { id: "devtools", label: "Dev Tools", icon: Wallet, perm: null, ceoOnly: true },
  { id: "vault", label: "Secret Vault", icon: KeyRound, perm: null, ceoOnly: true },
  { id: "branding", label: "Login Animation", icon: Sparkles, perm: "settings.manage" },
  { id: "emaillogo", label: "Email Logo", icon: Mail, perm: "settings.manage" },
  { id: "carddesign", label: "Card Design", icon: CreditCard, perm: "settings.manage" },
  { id: "resolution", label: "Fix Customer Issue", icon: LifeBuoy, perm: "customers.read" },
  { id: "users", label: "Customers", icon: Users, perm: "customers.read" },
  { id: "guard", label: "Restrictions & Appeals", icon: Ban, perm: "customers.read" },
  { id: "transactions", label: "Transactions", icon: Receipt, perm: "transactions.read" },
  { id: "fraud", label: "Fraud", icon: Fingerprint, perm: "audit.view" },
  { id: "kyc", label: "KYC & Compliance", icon: ShieldCheck, perm: "kyc.review" },
  { id: "badge", label: "Badges", icon: BadgeCheck, perm: "settings.manage" },
  { id: "providers", label: "Providers", icon: Plug, perm: "settings.manage" },
  { id: "stripe", label: "Stripe", icon: CreditCard, perm: "settings.manage" },
  { id: "fees", label: "Fees", icon: Wallet, perm: "settings.manage" },
  { id: "maintenance", label: "Maintenance", icon: Power, perm: "settings.manage" },
  { id: "social", label: "Social Media", icon: Share2, perm: "settings.manage" },
  { id: "team", label: "Team & Access", icon: UsersRound, perm: "team.invite" },
  { id: "support", label: "Live Agent Inbox", icon: MessageSquare, perm: "conversations.read" },
  { id: "approvals", label: "Approvals", icon: CheckCircle2, perm: "withdrawals.approve" },
  { id: "health", label: "API Health", icon: ActivityIcon, perm: "tech.read" },
  { id: "audit", label: "Audit Logs", icon: ScrollText, perm: "audit.view" },
  { id: "risk", label: "Risk", icon: ShieldAlert, perm: "audit.view" },
];

function EmailLogoTab() {
  const [state, setState] = useState({ has_logo: false, logo_url: null, align: "left" });
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(Date.now());
  const [cropSrc, setCropSrc] = useState(null);

  const load = () => api.get("/admin/branding/email-logo").then(({ data }) => setState({ align: "left", ...data })).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5 MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const uploadFile = async (file) => {
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo too large (max 2 MB) — try a tighter crop"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/branding/email-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setState({ align: "left", ...data }); setBust(Date.now()); setCropSrc(null);
      toast.success("Email logo updated");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const setAlignment = async (align) => {
    setState((s) => ({ ...s, align }));
    try { await api.put("/admin/branding/email-logo/align", { align }); }
    catch (e) { toast.error(errMsg(e)); load(); }
  };

  const remove = async () => {
    if (!window.confirm("Remove the custom email logo and go back to the default Jack Cash mark?")) return;
    setBusy(true);
    try { const { data } = await api.delete("/admin/branding/email-logo"); setState({ align: "left", ...data }); toast.success("Reverted to default logo"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const preview = state.logo_url ? `${state.logo_url}&c=${bust}` : null;
  const justify = state.align === "center" ? "justify-center" : state.align === "right" ? "justify-end" : "justify-start";

  return (
    <div className="max-w-lg" data-testid="email-logo-tab">
      <h3 className="font-display text-lg font-semibold mb-1">Email logo</h3>
      <p className="text-sm text-muted-foreground mb-4">This logo appears at the top of every email Jack Cash sends (verification codes, receipts, security alerts). Crop it, drop the white background, and place it left, centered or right. Max 2 MB.</p>

      <div className="rounded-2xl border border-border bg-[#faf9ff] p-6 mb-4">
        <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-3">Email header preview</div>
        <div className="rounded-xl bg-white border border-border p-5">
          <div className={`flex ${justify}`}>
            {preview ? (
              <img data-testid="email-logo-preview" src={preview} alt="Email logo" className="h-11 object-contain" />
            ) : (
              <div className="flex items-center gap-3">
                <div className="grid place-items-center w-11 h-11 rounded-xl bg-primary text-white font-extrabold text-2xl">J</div>
                <span className="font-extrabold text-xl text-primary tracking-tight">Jack Cash<sup className="text-[10px]">®</sup></span>
              </div>
            )}
          </div>
          <div className="mt-4 h-2 w-40 rounded bg-secondary" />
          <div className="mt-2 h-2 w-56 rounded bg-secondary" />
        </div>
      </div>

      {state.has_logo && (
        <div className="flex items-center gap-2 mb-4" data-testid="email-logo-align">
          <span className="text-xs text-muted-foreground w-16">Position</span>
          {[["Left", "left"], ["Center", "center"], ["Right", "right"]].map(([l, a]) => (
            <button key={a} data-testid={`email-logo-align-${a}`} onClick={() => setAlignment(a)}
              className={`flex-1 h-9 rounded-lg text-xs font-semibold transition-colors ${state.align === a ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>{l}</button>
          ))}
        </div>
      )}

      <label data-testid="email-logo-upload" className={`w-full h-12 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`}>
        {busy ? <Spinner className="w-5 h-5" /> : <><Mail size={16} /> {state.has_logo ? "Replace logo" : "Upload logo"}</>}
        <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={onFile} disabled={busy} />
      </label>

      {state.has_logo && (
        <button data-testid="email-logo-remove" onClick={remove} disabled={busy}
          className="w-full h-11 mt-2 rounded-xl bg-destructive/10 text-destructive font-semibold flex items-center justify-center gap-2">
          <Trash2 size={16} /> Remove logo (use default)
        </button>
      )}

      <CropModal open={!!cropSrc} image={cropSrc} busy={busy}
        onClose={() => setCropSrc(null)} onDone={uploadFile} />
    </div>
  );
}


function BrandingTab() {
  const DEFAULTS = { logo_scale: 1.0, text_scale: 1.0, logo_y: 0, text_y: 0, color: "#22C55E", text: "Jack Cash", sound_enabled: true };
  const SWATCHES = ["#22C55E", "#16A34A", "#FFC24B", "#F59E0B", "#5FD1FF", "#FF4FD8", "#FFFFFF"];
  const [cfg, setCfg] = useState(DEFAULTS);
  const [type, setType] = useState("login");
  const [replayKey, setReplayKey] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/branding/splash").then(({ data }) => setCfg({ ...DEFAULTS, ...data })).catch(() => {});
  }, []);

  const upd = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const clamp = (v, lo, hi) => Math.max(lo, min(v, hi));
  function min(a, b) { return a < b ? a : b; }
  const replay = () => setReplayKey((k) => k + 1);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/branding/splash", cfg);
      setCfg({ ...DEFAULTS, ...data });
      try { localStorage.setItem("jc_splash_cfg", JSON.stringify({ ...DEFAULTS, ...data })); } catch { /* ignore */ }
      toast.success("Animation saved");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const reset = () => { setCfg(DEFAULTS); replay(); };

  const Stepper = ({ label, k, step, lo, hi, fmt, testid }) => (
    <div className="flex items-center justify-between py-2.5 border-b border-border">
      <span className="text-sm font-medium">{label}</span>
      <div className="flex items-center gap-2">
        <button data-testid={`${testid}-minus`} onClick={() => { upd(k, clamp(+(cfg[k] - step).toFixed(2), lo, hi)); replay(); }}
          className="grid place-items-center w-8 h-8 rounded-lg bg-secondary text-lg font-bold">−</button>
        <span data-testid={`${testid}-value`} className="w-14 text-center text-sm font-semibold tabular-nums">{fmt(cfg[k])}</span>
        <button data-testid={`${testid}-plus`} onClick={() => { upd(k, clamp(+(cfg[k] + step).toFixed(2), lo, hi)); replay(); }}
          className="grid place-items-center w-8 h-8 rounded-lg bg-secondary text-lg font-bold">+</button>
      </div>
    </div>
  );

  return (
    <div className="max-w-lg" data-testid="branding-tab">
      <h2 className="font-display text-lg font-semibold mb-1">Login &amp; Logout animation</h2>
      <p className="text-sm text-muted-foreground mb-4">Customize the splash users see when they sign in and out.</p>

      {/* Preview */}
      <div className="relative rounded-2xl overflow-hidden mb-4" style={{ background: "#07030F", height: 330 }} data-testid="splash-preview">
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
          {["login", "logout"].map((t) => (
            <button key={t} data-testid={`splash-${t}`} onClick={() => { setType(t); replay(); }}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${type === t ? "bg-white text-black" : "bg-white/10 text-white/80"}`}>{t}</button>
          ))}
          <button data-testid="splash-replay" onClick={replay} className="grid place-items-center w-7 h-7 rounded-full bg-white/10 text-white">▶</button>
        </div>
        <div className="absolute inset-0 grid place-items-center">
          <SplashDesign key={replayKey} cfg={cfg} type={type} loop />
        </div>
      </div>

      {/* Controls */}
      <div className="rounded-2xl bg-card border border-border p-4">
        <Stepper label="Logo size" k="logo_scale" step={0.05} lo={0.5} hi={1.8} fmt={(v) => `${Math.round(v * 100)}%`} testid="splash-logo-size" />
        <Stepper label="Logo position ↕" k="logo_y" step={4} lo={-80} hi={80} fmt={(v) => `${v}px`} testid="splash-logo-y" />
        <Stepper label="Text size" k="text_scale" step={0.05} lo={0.5} hi={1.8} fmt={(v) => `${Math.round(v * 100)}%`} testid="splash-text-size" />
        <Stepper label="Text position ↕" k="text_y" step={4} lo={-80} hi={80} fmt={(v) => `${v}px`} testid="splash-text-y" />

        <div className="py-2.5 border-b border-border">
          <div className="text-sm font-medium mb-2">Brand text</div>
          <input data-testid="splash-text-input" maxLength={20} value={cfg.text} onChange={(e) => upd("text", e.target.value)}
            placeholder="Jack Cash" className="w-full h-11 px-3 rounded-xl bg-secondary outline-none focus:ring-2 focus:ring-primary/30" />
        </div>

        <div className="py-3">
          <div className="text-sm font-medium mb-2">Color</div>
          <div className="flex gap-2 flex-wrap">
            {SWATCHES.map((hex) => (
              <button key={hex} data-testid={`splash-color-${hex.replace("#", "")}`} onClick={() => { upd("color", hex); replay(); }}
                className={`w-9 h-9 rounded-full transition-transform ${cfg.color.toUpperCase() === hex.toUpperCase() ? "ring-2 ring-white ring-offset-2 ring-offset-card scale-110" : ""}`}
                style={{ background: hex, border: "1px solid rgba(255,255,255,0.15)" }} />
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between py-3 border-t border-border">
          <div>
            <div className="text-sm font-medium">Sound effects</div>
            <div className="text-xs text-muted-foreground">Play a chime on sign in &amp; sign out</div>
          </div>
          <button data-testid="splash-sound-toggle" onClick={() => upd("sound_enabled", !cfg.sound_enabled)}
            className={`relative w-12 h-7 rounded-full transition-colors ${cfg.sound_enabled ? "bg-primary" : "bg-secondary"}`}>
            <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${cfg.sound_enabled ? "left-6" : "left-1"}`} />
          </button>
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        <button data-testid="splash-reset" onClick={reset} className="flex-1 h-12 rounded-xl bg-secondary font-semibold">Reset</button>
        <button data-testid="splash-save" onClick={save} disabled={busy}
          className="flex-1 h-12 rounded-xl bg-primary text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? "Saving…" : "Save animation"}
        </button>
      </div>
    </div>
  );
}

function DevToolsTab() {
  const [wallet, setWallet] = useState(null);
  const [amount, setAmount] = useState("1000");
  const [busy, setBusy] = useState(false);
  const [holdings, setHoldings] = useState([]);
  const [cryptoAsset, setCryptoAsset] = useState("BTC");
  const [cryptoAmt, setCryptoAmt] = useState("0.5");

  const load = async () => {
    try { const { data } = await api.get("/admin/dev/wallet"); setWallet(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const loadCrypto = async () => {
    try { const { data } = await api.get("/admin/dev/crypto"); setHoldings(data.holdings || []); }
    catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); loadCrypto(); }, []);

  const credit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter an amount");
    setBusy(true);
    try { const { data } = await api.post("/admin/dev/credit", { amount: amt }); setWallet(data); toast.success("Test funds added"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset your test balance to $0 and remove test funds?")) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/dev/reset"); setWallet(data); toast.success("Test funds removed"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const clearActivity = async () => {
    if (!window.confirm("Delete ALL transactions from your activity feed? Your balance stays the same. This cannot be undone.")) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/dev/clear-activity"); toast.success(`Cleared ${data.deleted} item(s) from activity`); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const creditCrypto = async () => {
    const amt = parseFloat(cryptoAmt);
    if (!amt || amt <= 0) return toast.error("Enter a crypto amount");
    setBusy(true);
    try { const { data } = await api.post("/admin/dev/crypto/credit", { asset: cryptoAsset, amount: amt }); setHoldings(data.holdings || []); toast.success(`Added ${amt} ${cryptoAsset}`); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const resetCrypto = async () => {
    if (!window.confirm("Remove all test crypto holdings?")) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/dev/crypto/reset"); setHoldings(data.holdings || []); toast.success("Test crypto removed"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const CRYPTO_ASSETS = ["BTC", "ETH", "USDC", "USDT", "SOL"];

  return (
    <div className="max-w-lg" data-testid="devtools-tab">
      <div className="rounded-2xl p-5 mb-4 text-white" style={{ background: "linear-gradient(135deg,#4C1D95,#1B1340)" }}>
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-white/70 mb-1">
          <Lock size={13} /> CEO only · hidden from staff
        </div>
        <div className="text-sm text-white/80">Add test funds to your own wallet so you can try the live app, then remove them when you're done. This is only for your CEO account.</div>
      </div>

      <div className="rounded-2xl bg-card border border-border p-5">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Your current balance</div>
        <div className="amount-figure text-3xl tabular-nums mt-1" data-testid="devtools-balance">{wallet ? wallet.balance_display : "—"}</div>

        <label className="block text-xs font-semibold text-muted-foreground mt-5 mb-1">Amount to add ($)</label>
        <div className="flex gap-2">
          <input data-testid="devtools-amount" type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)}
            className="flex-1 h-12 px-3 rounded-xl bg-secondary outline-none focus:ring-2 focus:ring-primary/30" />
          <button data-testid="devtools-add" onClick={credit} disabled={busy}
            className="px-5 h-12 rounded-xl bg-primary text-white font-semibold disabled:opacity-60">Add</button>
        </div>
        <div className="flex gap-2 mt-2">
          {[100, 1000, 10000, 100000].map((v) => (
            <button key={v} onClick={() => setAmount(String(v))} className="flex-1 h-9 rounded-lg bg-secondary text-sm font-semibold text-muted-foreground hover:text-foreground">${v.toLocaleString()}</button>
          ))}
        </div>

        <button data-testid="devtools-reset" onClick={reset} disabled={busy}
          className="w-full h-11 mt-5 rounded-xl bg-destructive/10 text-destructive font-semibold flex items-center justify-center gap-2">
          <RefreshCw size={16} /> Remove all test funds (reset to $0)
        </button>

        <button data-testid="devtools-clear-activity" onClick={clearActivity} disabled={busy}
          className="w-full h-11 mt-2 rounded-xl bg-destructive/10 text-destructive font-semibold flex items-center justify-center gap-2">
          <Trash2 size={16} /> Clear all activity (delete transfers)
        </button>
      </div>

      {/* Test crypto holdings */}
      <div className="rounded-2xl bg-card border border-border p-5 mt-4" data-testid="devtools-crypto">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Test crypto holdings</div>
        {holdings.length > 0 ? (
          <div className="mt-2 space-y-1.5">
            {holdings.map((h) => (
              <div key={h.asset} className="flex items-center justify-between text-sm" data-testid={`devtools-holding-${h.asset}`}>
                <span className="font-medium">{h.name || h.asset}</span>
                <span className="tabular-nums font-semibold">{Number(h.amount).toLocaleString(undefined, { maximumFractionDigits: 8 })} {h.asset}</span>
              </div>
            ))}
          </div>
        ) : <div className="text-sm text-muted-foreground mt-1">No test crypto yet.</div>}

        <label className="block text-xs font-semibold text-muted-foreground mt-5 mb-1">Add test crypto</label>
        <div className="flex gap-2">
          <select data-testid="devtools-crypto-asset" value={cryptoAsset} onChange={(e) => setCryptoAsset(e.target.value)}
            className="w-24 h-12 px-3 rounded-xl bg-secondary outline-none focus:ring-2 focus:ring-primary/30">
            {CRYPTO_ASSETS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <input data-testid="devtools-crypto-amount" type="number" min="0" step="any" value={cryptoAmt} onChange={(e) => setCryptoAmt(e.target.value)}
            className="flex-1 h-12 px-3 rounded-xl bg-secondary outline-none focus:ring-2 focus:ring-primary/30" />
          <button data-testid="devtools-crypto-add" onClick={creditCrypto} disabled={busy}
            className="px-5 h-12 rounded-xl bg-primary text-white font-semibold disabled:opacity-60">Add</button>
        </div>

        <button data-testid="devtools-crypto-reset" onClick={resetCrypto} disabled={busy}
          className="w-full h-11 mt-4 rounded-xl bg-destructive/10 text-destructive font-semibold flex items-center justify-center gap-2">
          <RefreshCw size={16} /> Remove all test crypto
        </button>
      </div>
    </div>
  );
}

function SecretRow({ item, onDelete }) {
  const [revealed, setRevealed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const reveal = async () => {
    if (revealed) { setRevealed(null); return; }
    setBusy(true);
    try { const { data } = await api.post(`/admin/vault/${item.secret_id}/reveal`); setRevealed(data.value); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    try {
      let v = revealed;
      if (!v) { const { data } = await api.post(`/admin/vault/${item.secret_id}/reveal`); v = data.value; }
      await navigator.clipboard.writeText(v);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
      toast.success("Kopye");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="rounded-xl bg-secondary/60 border border-border p-3" data-testid={`vault-row-${item.secret_id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate" data-testid={`vault-name-${item.secret_id}`}>{item.name}</div>
          <div className="text-[11px] font-mono text-primary">{item.secret_id}</div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button data-testid={`vault-reveal-${item.secret_id}`} onClick={reveal} disabled={busy}
            className="w-9 h-9 rounded-lg bg-background flex items-center justify-center text-muted-foreground hover:text-foreground">
            {revealed ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
          <button data-testid={`vault-copy-${item.secret_id}`} onClick={copy}
            className="w-9 h-9 rounded-lg bg-background flex items-center justify-center text-muted-foreground hover:text-foreground">
            {copied ? <Check size={15} className="text-green-500" /> : <Copy size={15} />}
          </button>
          <button data-testid={`vault-delete-${item.secret_id}`} onClick={() => onDelete(item.secret_id)}
            className="w-9 h-9 rounded-lg bg-background flex items-center justify-center text-destructive">
            <Trash2 size={15} />
          </button>
        </div>
      </div>
      <div className="mt-2 font-mono text-xs break-all bg-background rounded-lg px-3 py-2 text-muted-foreground" data-testid={`vault-value-${item.secret_id}`}>
        {revealed ?? item.value_masked}
      </div>
    </div>
  );
}

function VaultSection({ title, hint, section, items, onChanged }) {
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!name.trim()) return toast.error("Mete yon non");
    if (!value.trim()) return toast.error("Mete valè a");
    setBusy(true);
    try {
      await api.post("/admin/vault", { section, name: name.trim(), value: value.trim() });
      setName(""); setValue(""); toast.success("Anrejistre");
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Efase sekrè sa a?")) return;
    try { await api.delete(`/admin/vault/${id}`); toast.success("Efase"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="rounded-2xl bg-card border border-border p-5" data-testid={`vault-section-${section}`}>
      <div className="flex items-center gap-2 mb-1">
        <KeyRound size={16} className="text-primary" />
        <h3 className="font-semibold">{title}</h3>
        <span className="ml-auto text-xs text-muted-foreground">{items.length}</span>
      </div>
      <p className="text-xs text-muted-foreground mb-4">{hint}</p>

      <div className="space-y-2 mb-4">
        <input data-testid={`vault-input-name-${section}`} value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Non (egz: Bridge API Key)"
          className="w-full h-11 px-3 rounded-xl bg-secondary outline-none focus:ring-2 focus:ring-primary/30 text-sm" />
        <div className="flex gap-2">
          <input data-testid={`vault-input-value-${section}`} value={value} onChange={(e) => setValue(e.target.value)}
            placeholder="Valè (kle / token an)"
            className="flex-1 h-11 px-3 rounded-xl bg-secondary outline-none focus:ring-2 focus:ring-primary/30 text-sm font-mono" />
          <button data-testid={`vault-add-${section}`} onClick={add} disabled={busy}
            className="px-4 h-11 rounded-xl bg-primary text-white font-semibold flex items-center gap-1.5 disabled:opacity-60">
            <Plus size={16} /> Ajoute
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {items.length === 0 && <div className="text-xs text-muted-foreground py-4 text-center">Pa gen anyen anrejistre poko.</div>}
        {items.map((it) => <SecretRow key={it.secret_id} item={it} onDelete={del} />)}
      </div>
    </div>
  );
}

function VaultTab() {
  const [data, setData] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/admin/vault"); setData(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);
  if (!data) return <Spinner />;

  return (
    <div className="max-w-2xl" data-testid="vault-tab">
      <div className="rounded-2xl p-5 mb-4 text-white" style={{ background: "linear-gradient(135deg,#4C1D95,#1B1340)" }}>
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-white/70 mb-1">
          <Lock size={13} /> CEO sèlman · kache pou staff
        </div>
        <div className="text-sm text-white/80">Mete API keys ak tokens ou yo isit la an sekirite — ou pa bezwen voye yo nan chat la. Chak sekrè gen yon non ak yon idantifikasyon otomatik. Valè yo rete kache; itilize je a pou montre oswa bouton kopi a.</div>
      </div>

      <div className="grid gap-4">
        <VaultSection title="API Keys" section="api_key"
          hint="Kle sèvis yo (Bridge, Twilio, Transak, elatriye)."
          items={data.api_keys} onChanged={load} />
        <VaultSection title="Tokens" section="token"
          hint="Nenpòt lòt token oswa sekrè."
          items={data.tokens} onChanged={load} />
      </div>
    </div>
  );
}

export default function Admin() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [access, setAccess] = useState(null);
  const [pin, setPin] = useState(null);       // pin status
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [a, p] = await Promise.all([api.get("/admin/me/access"), api.get("/admin/pin/status")]);
        setAccess(a.data); setPin(p.data);
      } catch (e) { toast.error(errMsg(e)); }
    })();
  }, []);

  if (!access || !pin) return <FullLoader />;

  const can = (perm) => !perm || access.permissions.includes("*") || access.permissions.includes(perm);
  const visibleTabs = TABS.filter((t) => can(t.perm) && (!t.ceoOnly || access.is_ceo));

  // PIN gate: must set a PIN (first time) or verify it before using the panel.
  if (!unlocked && (!pin.has_pin || pin.reset_required || pin.has_pin)) {
    return <PinGate pin={pin} onDone={() => setUnlocked(true)} onExit={() => nav("/app")} roleLabel={access.role_label} />;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="glass border-b border-border px-5 md:px-8 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <Logo size={32} />
          <span data-testid="admin-badge" className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.15em] uppercase text-white px-2.5 py-1 rounded-full" style={{ background: "#2563EB" }}>
            <ShieldCheck size={12} /> ADMIN
          </span>
          <span className="hidden sm:inline text-xs font-semibold text-muted-foreground" data-testid="admin-role-label">— {access.role_label}</span>
        </div>
        <button data-testid="admin-exit-btn" onClick={() => nav("/app")} className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground">
          <ArrowLeft size={16} /> Back to app
        </button>
      </header>

      <div className="max-w-6xl mx-auto px-5 md:px-8 py-6">
        <h1 className="font-display text-3xl font-semibold tracking-tight">Admin Dashboard</h1>
        <p className="text-muted-foreground mb-6">Signed in as {user.email} · <span className="text-primary font-semibold">{access.role_label}</span></p>

        <div className="flex gap-2 overflow-x-auto no-scrollbar mb-6 border-b border-border">
          {visibleTabs.map(({ id, label, icon: Icon }) => (
            <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${tab === id ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        {tab === "overview" && <Overview onFixIssue={can("customers.read") ? () => setTab("resolution") : null} />}
        {tab === "analytics" && can("settings.manage") && <AnalyticsTab />}
        {tab === "devtools" && access.is_ceo && <DevToolsTab />}
        {tab === "vault" && access.is_ceo && <VaultTab />}
        {tab === "branding" && can("settings.manage") && <BrandingTab />}
        {tab === "emaillogo" && can("settings.manage") && <EmailLogoTab />}
        {tab === "carddesign" && can("settings.manage") && <CardDesignTab />}
        {tab === "resolution" && can("customers.read") && <ResolutionTab access={access} />}
        {tab === "users" && <UsersTab />}
        {tab === "guard" && can("customers.read") && <GuardTab canEdit={can("customers.edit")} />}
        {tab === "transactions" && <TransactionsTab />}
        {tab === "fraud" && <FraudTab />}
        {tab === "kyc" && <KycTab />}
        {tab === "badge" && <BadgeTab />}
        {tab === "providers" && <ProvidersTab />}
        {tab === "stripe" && <StripeTab />}
        {tab === "fees" && <FeesTab />}
        {tab === "maintenance" && can("settings.manage") && <MaintenanceTab />}
        {tab === "social" && can("settings.manage") && <SocialTab />}
        {tab === "team" && can("team.invite") && <TeamTab isCeo={access.is_ceo} />}
        {tab === "support" && can("conversations.read") && <InboxTab access={access} />}
        {tab === "approvals" && can("withdrawals.approve") && <ApprovalsTab />}
        {tab === "health" && <HealthTab />}
        {tab === "audit" && <ListTab endpoint="/admin/audit-logs" cols={["created_at", "action", "user_id"]} testid="audit" />}
        {tab === "risk" && <ListTab endpoint="/admin/risk-events" cols={["created_at", "severity", "kind", "description"]} testid="risk" />}
      </div>
    </div>
  );
}

function PinReset({ onBack }) {
  const [step, setStep] = useState(1);
  const [info, setInfo] = useState(null);
  const [challengeId, setChallengeId] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [code, setCode] = useState("");
  const [pin1, setPin1] = useState("");
  const [pin2, setPin2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/pin/reset/info"); setInfo(data); }
      catch (e) { toast.error(errMsg(e)); onBack(); }
    })();
  }, [onBack]);

  const sendCode = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/pin/reset/request");
      setChallengeId(data.challenge_id); setEmailHint(data.email_hint);
      toast.success("Code sent to your email");
      setStep(2);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const confirmReset = async () => {
    if (pin1.length !== 4) return toast.error("Enter a 4-digit code");
    if (pin1 !== pin2) return toast.error("The two codes don't match");
    setBusy(true);
    try {
      await api.post("/admin/pin/reset/confirm", { challenge_id: challengeId, code, pin: pin1 });
      toast.success("Your admin code was changed");
      onBack();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const InfoRow = ({ label, value }) => (
    <div className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold font-mono">{value || "—"}</span>
    </div>
  );

  return (
    <div className="min-h-screen grid place-items-center bg-background px-6" data-testid="admin-pin-reset">
      <div className="w-full max-w-sm text-center">
        <div className="mx-auto grid place-items-center w-16 h-16 rounded-2xl text-white mb-4" style={{ background: "#4C1D95" }}><KeyRound size={30} /></div>

        {step === 1 && (
          <>
            <h1 className="font-display text-2xl font-semibold">Reset your admin code</h1>
            <p className="text-sm text-muted-foreground mt-1">Confirm it's really you, then set a new 4-digit code.</p>
            {!info ? <div className="mt-8 grid place-items-center"><Spinner className="w-6 h-6 text-primary" /></div> : (
              <div className="mt-6 text-left rounded-2xl bg-card border border-border p-4" data-testid="reset-info">
                <InfoRow label="Admin ID" value={info.admin_id} />
                <InfoRow label="Username" value={info.username ? `@${info.username}` : ""} />
                <InfoRow label="Phone" value={info.phone} />
                <InfoRow label="Email" value={info.email} />
                <InfoRow label="Role" value={info.role_label} />
              </div>
            )}
            <button data-testid="reset-send-code-btn" onClick={sendCode} disabled={busy || !info}
              className="w-full h-13 mt-5 py-3 rounded-2xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "#6D28D9" }}>
              {busy ? <Spinner className="w-5 h-5" /> : <><Mail size={17} /> Confirm email & send code</>}
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <h1 className="font-display text-2xl font-semibold">Enter the code</h1>
            <p className="text-sm text-muted-foreground mt-1">We emailed a 6-digit code to {emailHint}.</p>
            <input data-testid="reset-email-code-input" autoFocus inputMode="numeric" maxLength={6} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="mt-6 w-full h-16 text-center text-3xl tracking-[0.5em] font-mono rounded-2xl bg-secondary border border-transparent focus:border-primary outline-none" placeholder="••••••" />
            <button data-testid="reset-verify-btn" onClick={() => code.length === 6 ? setStep(3) : toast.error("Enter the 6-digit code")}
              className="w-full h-13 mt-4 py-3 rounded-2xl text-white font-semibold flex items-center justify-center gap-2" style={{ background: "#6D28D9" }}>Continue</button>
            <button onClick={sendCode} disabled={busy} className="mt-3 text-sm font-semibold text-primary">Resend code</button>
          </>
        )}

        {step === 3 && (
          <>
            <h1 className="font-display text-2xl font-semibold">Set a new 4-digit code</h1>
            <p className="text-sm text-muted-foreground mt-1">Enter it twice. Both must match.</p>
            <input data-testid="reset-new-pin-input" autoFocus inputMode="numeric" maxLength={4} value={pin1} type="password"
              onChange={(e) => setPin1(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className="mt-6 w-full h-16 text-center text-3xl tracking-[0.6em] font-mono rounded-2xl bg-secondary border border-transparent focus:border-primary outline-none" placeholder="••••" />
            <input data-testid="reset-confirm-pin-input" inputMode="numeric" maxLength={4} value={pin2} type="password"
              onChange={(e) => setPin2(e.target.value.replace(/\D/g, "").slice(0, 4))}
              onKeyDown={(e) => e.key === "Enter" && confirmReset()}
              className="mt-3 w-full h-16 text-center text-3xl tracking-[0.6em] font-mono rounded-2xl bg-secondary border border-transparent focus:border-primary outline-none" placeholder="••••" />
            <button data-testid="reset-confirm-btn" onClick={confirmReset} disabled={busy}
              className="w-full h-13 mt-4 py-3 rounded-2xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "#6D28D9" }}>
              {busy ? <Spinner className="w-5 h-5" /> : "Save new code"}
            </button>
          </>
        )}

        <button onClick={onBack} data-testid="reset-back-btn" className="mt-4 text-sm text-muted-foreground">Back</button>
      </div>
    </div>
  );
}

function PinGate({ pin, onDone, onExit, roleLabel }) {
  const firstTime = !pin.has_pin || pin.reset_required;
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [reset, setReset] = useState(false);
  const submit = async () => {
    if (!/^\d{4}$/.test(val)) return toast.error("Enter a 4-digit PIN");
    setBusy(true);
    try {
      if (firstTime) { await api.post("/admin/pin/set", { pin: val }); toast.success("PIN set"); }
      else { await api.post("/admin/pin/verify", { pin: val }); }
      onDone();
    } catch (e) { toast.error(errMsg(e)); setVal(""); }
    finally { setBusy(false); }
  };
  if (reset) return <PinReset onBack={() => setReset(false)} />;
  return (
    <div className="min-h-screen grid place-items-center bg-background px-6">
      <div className="w-full max-w-sm text-center" data-testid="admin-pin-gate">
        <div className="mx-auto grid place-items-center w-16 h-16 rounded-2xl text-white mb-4" style={{ background: "#4C1D95" }}><KeyRound size={30} /></div>
        <h1 className="font-display text-2xl font-semibold">{firstTime ? "Set your admin PIN" : "Enter your admin PIN"}</h1>
        <p className="text-sm text-muted-foreground mt-1">{roleLabel} · secure access</p>
        <input data-testid="admin-pin-input" autoFocus inputMode="numeric" maxLength={4} value={val}
          onChange={(e) => setVal(e.target.value.replace(/\D/g, "").slice(0, 4))}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          className="mt-6 w-full h-16 text-center text-3xl tracking-[0.6em] font-mono rounded-2xl bg-secondary border border-transparent focus:border-primary outline-none" placeholder="••••" />
        <button data-testid="admin-pin-submit" onClick={submit} disabled={busy}
          className="w-full h-13 mt-4 py-3 rounded-2xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "#6D28D9" }}>
          {busy ? <Spinner className="w-5 h-5" /> : (firstTime ? "Set PIN & continue" : "Unlock")}
        </button>
        {!firstTime && (
          <button onClick={() => setReset(true)} data-testid="pin-reset-link" className="mt-3 block mx-auto text-sm font-semibold text-primary">Forgot your code? Reset it</button>
        )}
        <button onClick={onExit} className="mt-2 text-sm text-muted-foreground">Back to app</button>
      </div>
    </div>
  );
}

function useFetch(url) {
  const [data, setData] = useState(null);
  useEffect(() => { (async () => { try { const r = await api.get(url); setData(r.data); } catch (e) { toast.error(errMsg(e)); } })(); }, [url]);
  return [data, setData];
}

function Overview({ onFixIssue }) {
  const [stats] = useFetch("/admin/stats");
  if (!stats) return <FullLoader />;
  const cards = [
    { label: "Total Users", value: stats.users, icon: Users },
    { label: "Transactions", value: stats.transactions, icon: Receipt },
    { label: "Platform Balance", value: stats.total_balance_display, icon: Wallet },
    { label: "Failed", value: stats.failed_transactions, icon: AlertTriangle },
    { label: "Pending", value: stats.pending_transactions, icon: ActivityIcon },
    { label: "Risk Events", value: stats.risk_events, icon: ShieldAlert },
  ];
  return (
    <div className="space-y-5">
      {onFixIssue && (
        <button data-testid="overview-fix-issue-btn" onClick={onFixIssue}
          className="w-full flex items-center justify-between gap-3 rounded-2xl p-5 text-white text-left transition-transform hover:scale-[1.01]"
          style={{ background: "linear-gradient(135deg,#4B1D86,#2A0E5C)" }}>
          <div className="flex items-center gap-3">
            <div className="grid place-items-center w-11 h-11 rounded-xl bg-white/15"><LifeBuoy size={22} /></div>
            <div>
              <div className="font-display font-semibold text-lg">Fix Customer Issue</div>
              <div className="text-sm text-white/70">Search a customer and resolve their problem — permission-aware.</div>
            </div>
          </div>
          <ArrowLeft size={18} className="rotate-180 opacity-80" />
        </button>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {cards.map(({ label, value, icon: Icon }) => (
          <div key={label} data-testid={`stat-${label}`} className="rounded-2xl bg-card border border-border p-5">
            <Icon size={20} className="text-primary" />
            <div className="amount-figure text-3xl mt-3">{value}</div>
            <div className="text-sm text-muted-foreground">{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useFetch("/admin/users");
  const [adjust, setAdjust] = useState(null);
  const [restrict, setRestrict] = useState(null);
  if (!users) return <FullLoader />;

  const setStatus = async (id, status) => {
    try { await api.put(`/admin/users/${id}/status`, { status }); setUsers(users.map((u) => u.user_id === id ? { ...u, account_status: status } : u)); toast.success("Status updated"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const toggleVerify = async (u) => {
    const next = u.verification_status === "verified" ? "unverified" : "verified";
    try {
      await api.put(`/admin/users/${u.user_id}/verification`, { verification_status: next });
      setUsers(users.map((x) => x.user_id === u.user_id ? { ...x, verification_status: next } : x));
      toast.success(next === "verified" ? "User verified — green badge granted" : "Verification removed");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="rounded-2xl border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-muted-foreground"><tr>
            {["User", "Cash Tag", "Balance", "Status", "KYC", "Actions"].map((h) => <th key={h} className="text-left font-semibold px-4 py-3">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-border">
            {users.map((u) => (
              <tr key={u.user_id} data-testid={`admin-user-${u.user_id}`}>
                <td className="px-4 py-3"><div className="font-medium flex items-center gap-1.5">{u.display_name}{u.badge_active && <VerifiedBadge color={u.badge_color} size={14} />}</div><div className="text-xs text-muted-foreground">{u.email}</div></td>
                <td className="px-4 py-3 font-mono text-xs">{u.cash_tag}</td>
                <td className="px-4 py-3 font-mono">{u.balance_display}</td>
                <td className="px-4 py-3">
                  <span className="capitalize">{u.account_status}</span>
                  {(u.restriction_count || 0) > 0 && (
                    <span data-testid={`user-restrictions-${u.user_id}`} className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600">
                      {u.restriction_count}/5
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 capitalize text-xs">{u.verification_status}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2 items-center">
                    <select data-testid={`user-status-select-${u.user_id}`} value={u.account_status} onChange={(e) => setStatus(u.user_id, e.target.value)}
                      className="bg-secondary rounded-lg px-2 py-1 text-xs outline-none">
                      <option value="active">active</option><option value="suspended">suspended</option><option value="frozen">frozen</option><option value="banned">banned</option>
                    </select>
                    <button data-testid={`user-verify-${u.user_id}`} onClick={() => toggleVerify(u)}
                      className={`text-xs font-semibold ${u.verification_status === "verified" ? "text-muted-foreground" : "text-success"}`}>
                      {u.verification_status === "verified" ? "Unverify" : "Verify"}
                    </button>
                    <button data-testid={`user-adjust-${u.user_id}`} onClick={() => setAdjust(u)} className="text-primary text-xs font-semibold">Adjust</button>
                    <button data-testid={`user-restrict-${u.user_id}`} onClick={() => setRestrict(u)} className="text-amber-600 text-xs font-semibold">Restrict</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {adjust && <AdjustModal user={adjust} onClose={() => setAdjust(null)} onDone={(bal) => { setUsers(users.map((u) => u.user_id === adjust.user_id ? { ...u, balance_display: bal } : u)); setAdjust(null); }} />}
      {restrict && <RestrictModal user={restrict} onClose={() => setRestrict(null)} onDone={(state) => { setUsers(users.map((u) => u.user_id === restrict.user_id ? { ...u, account_status: state.account_status, restriction_count: state.restriction_count } : u)); setRestrict(null); }} />}
    </div>
  );
}

function RestrictModal({ user, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${user.user_id}/restrict`, { reason });
      toast.success(data.banned ? "User reached 5 restrictions — account banned" : `Restriction added (${data.restriction_count}/5)`);
      onDone(data);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-card rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display font-semibold text-lg mb-1">Restrict account</h3>
        <p className="text-xs text-muted-foreground mb-4">{user.display_name} · {user.cash_tag}. Currently {user.restriction_count || 0}/5. At 5 the account is auto-banned. The user can submit an appeal.</p>
        <textarea data-testid="restrict-reason-input" placeholder="Reason for this restriction (shown to the user)" value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
          className="w-full p-3 rounded-xl bg-secondary outline-none text-sm resize-none mb-4" />
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 h-11 rounded-xl bg-secondary font-semibold">Cancel</button>
          <button data-testid="restrict-submit-btn" onClick={submit} disabled={busy || reason.trim().length < 3} className="flex-1 h-11 rounded-xl bg-amber-500 text-white font-semibold disabled:opacity-50">{busy ? "…" : "Add restriction"}</button>
        </div>
      </div>
    </div>
  );
}

function AdjustModal({ user, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${user.user_id}/adjust`, { amount: parseFloat(amount), reason });
      toast.success("Balance adjusted (auditable ledger entry created)");
      onDone(data.balance_display);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-card rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display font-semibold text-lg mb-1">Adjust balance</h3>
        <p className="text-xs text-muted-foreground mb-4">{user.display_name} · {user.cash_tag}. Positive credits, negative debits. Creates an auditable transaction.</p>
        <input data-testid="adjust-amount-input" type="number" step="0.01" placeholder="Amount (e.g. 50 or -10)" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full h-12 px-3 rounded-xl bg-secondary outline-none mb-3" />
        <input data-testid="adjust-reason-input" placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} className="w-full h-12 px-3 rounded-xl bg-secondary outline-none mb-4" />
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 h-11 rounded-xl bg-secondary font-semibold">Cancel</button>
          <button data-testid="adjust-submit-btn" onClick={submit} disabled={busy || !amount || !reason} className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground font-semibold disabled:opacity-50">{busy ? "…" : "Apply"}</button>
        </div>
      </div>
    </div>
  );
}

function TransactionsTab() {
  const [txns] = useFetch("/admin/transactions");
  if (!txns) return <FullLoader />;
  return (
    <div className="rounded-2xl border border-border overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-secondary text-muted-foreground"><tr>
          {["Type", "Amount", "Status", "From", "To", "Date"].map((h) => <th key={h} className="text-left font-semibold px-4 py-3">{h}</th>)}
        </tr></thead>
        <tbody className="divide-y divide-border">
          {txns.map((t) => (
            <tr key={t.transaction_id}>
              <td className="px-4 py-3 capitalize">{t.type}</td>
              <td className="px-4 py-3 font-mono">{t.amount_display}</td>
              <td className="px-4 py-3 capitalize">{t.status}</td>
              <td className="px-4 py-3 font-mono text-xs">{t.sender_cash_tag || "—"}</td>
              <td className="px-4 py-3 font-mono text-xs">{t.recipient_cash_tag || t.counterparty_tag || "—"}</td>
              <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(t.timestamp).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProvidersTab() {
  const [rows] = useFetch("/admin/providers");
  if (!rows) return <FullLoader />;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {rows.map((p) => (
        <div key={p.key} data-testid={`provider-${p.key}`} className="rounded-2xl bg-card border border-border p-5 flex items-center justify-between">
          <div>
            <div className="font-medium">{p.name}</div>
            <div className="text-xs text-muted-foreground">{p.category}</div>
          </div>
          <StatusBadge label={p.status_label} />
        </div>
      ))}
    </div>
  );
}

function StripeTab() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get("/admin/settings/stripe"); setInfo(data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);
  if (!info) return <FullLoader />;

  const Yes = ({ on, labelOn = "Enabled", labelOff = "Not set up" }) => (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${on ? "bg-success/10 text-success" : "bg-secondary text-muted-foreground"}`}>
      {on ? <CheckCircle2 size={13} /> : <XCircle size={13} />} {on ? labelOn : labelOff}
    </span>
  );

  const rows = [
    ["Account ID", info.account_id || info.account_id_env || "—"],
    ["Business name", info.business_name || "— (none set)"],
    ["Account email", info.email || "—"],
    ["Country", info.country || "—"],
    ["Account type", info.type || "—"],
    ["Secret key prefix", info.key_prefix || "—"],
  ];

  return (
    <div className="max-w-2xl space-y-4" data-testid="admin-stripe-tab">
      {/* Connection banner */}
      <div className={`rounded-2xl border p-5 ${info.connected ? "border-success/30 bg-success/5" : "border-destructive/30 bg-destructive/5"}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <CreditCard size={20} className={info.connected ? "text-success" : "text-destructive"} />
            <span className="font-display font-semibold text-lg">Stripe connection</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${info.mode === "live" ? "bg-[#2F80FF]/10 text-[#2F80FF]" : "bg-warning/10 text-warning"}`} data-testid="stripe-mode">
              {info.mode === "live" ? "LIVE" : info.mode === "test" ? "TEST / SANDBOX" : "UNKNOWN"}
            </span>
            <button data-testid="stripe-refresh-btn" onClick={load} disabled={busy} className="flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-xl bg-secondary disabled:opacity-60"><RefreshCw size={15} /> Refresh</button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-2" data-testid="stripe-connection-status">
          {info.connected
            ? "The app is connected to the Stripe account below. Verify it is your independent Jack Cash account."
            : `Not connected. ${info.error || "Check the Stripe keys in Secrets."}`}
        </p>
      </div>

      {/* Account details */}
      <div className="rounded-2xl bg-card border border-border overflow-hidden">
        <div className="px-5 py-3 border-b border-border font-display font-semibold text-sm">Connected account</div>
        <div className="divide-y divide-border">
          {rows.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between gap-4 px-5 py-3">
              <span className="text-sm text-muted-foreground">{k}</span>
              <span className="text-sm font-medium font-mono text-right break-all" data-testid={`stripe-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{String(v)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Capabilities */}
      <div className="rounded-2xl bg-card border border-border p-5 space-y-3">
        <div className="font-display font-semibold text-sm">Capabilities</div>
        <div className="flex items-center justify-between"><span className="text-sm">Card payments (charges)</span><Yes on={info.charges_enabled} /></div>
        <div className="flex items-center justify-between"><span className="text-sm">Payouts (withdrawals)</span><Yes on={info.payouts_enabled} /></div>
        <div className="flex items-center justify-between"><span className="text-sm">Card issuing (virtual cards)</span><Yes on={info.card_issuing === "active"} labelOn="Active" /></div>
        <div className="flex items-center justify-between"><span className="text-sm">Transfers (Connect)</span><Yes on={info.transfers === "active"} labelOn="Active" /></div>
      </div>

      {/* Guide */}
      <div className="rounded-2xl border border-border bg-secondary/40 p-5 text-sm">
        <div className="font-display font-semibold mb-2">How to connect your own Jack Cash account</div>
        <ol className="list-decimal pl-5 space-y-1 text-muted-foreground">
          <li>In Stripe, switch to the <b>Jack Cash LLC</b> account.</li>
          <li>Go to <b>Developers → API keys</b>.</li>
          <li>Copy the <b>Secret key</b> (<span className="font-mono">sk_…</span>) and <b>Publishable key</b> (<span className="font-mono">pk_…</span>).</li>
          <li>Add them in the platform <b>Secrets</b> as <span className="font-mono">STRIPE_SECRET_KEY</span> and <span className="font-mono">STRIPE_PUBLISHABLE_KEY</span>, then redeploy/restart.</li>
          <li>Come back here and press <b>Refresh</b> to confirm the right account is connected.</li>
        </ol>
      </div>
    </div>
  );
}

function TeamTab({ isCeo }) {
  const [team, setTeam] = useState(null);
  const [roles, setRoles] = useState([]);
  const [invite, setInvite] = useState({ email: "", admin_role: "support" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [t, r] = await Promise.all([api.get("/admin/team"), api.get("/admin/roles/catalogue")]);
      setTeam(t.data.team); setRoles(r.data.roles);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const doInvite = () => {
    if (!invite.email) return toast.error("Enter an email");
    act(() => api.post("/admin/team/invite", invite), "Member added — PIN setup emailed");
  };

  if (!team) return <FullLoader />;
  if (!isCeo) return <div className="text-sm text-muted-foreground" data-testid="team-no-ceo">Only the CEO can manage the team.</div>;

  const sel = "h-10 px-3 rounded-xl bg-secondary border border-transparent focus:border-primary outline-none text-sm";

  return (
    <div className="space-y-6" data-testid="team-tab">
      <div className="rounded-2xl bg-card border border-border p-5">
        <div className="font-display font-semibold mb-3 flex items-center gap-2"><UserCog size={18} className="text-primary" /> Invite a team member</div>
        <div className="flex flex-col sm:flex-row gap-3">
          <input data-testid="invite-email" placeholder="Existing Jack Cash account email" value={invite.email}
            onChange={(e) => setInvite({ ...invite, email: e.target.value })} className={sel + " flex-1"} />
          <select data-testid="invite-role" value={invite.admin_role} onChange={(e) => setInvite({ ...invite, admin_role: e.target.value })} className={sel}>
            {roles.filter((r) => r.id !== "ceo").map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
          <button data-testid="invite-submit" onClick={doInvite} disabled={busy} className="h-10 px-5 rounded-xl text-white font-semibold disabled:opacity-60" style={{ background: "#6D28D9" }}>Add</button>
        </div>
      </div>

      <div className="rounded-2xl bg-card border border-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="team-table">
          <thead><tr className="text-left text-muted-foreground border-b border-border">
            <th className="p-3">Name</th><th className="p-3">Admin ID</th><th className="p-3">Email</th><th className="p-3">Role</th>
            <th className="p-3">Badge</th><th className="p-3">PIN</th><th className="p-3">Status</th><th className="p-3">Actions</th>
          </tr></thead>
          <tbody>
            {team.map((m) => (
              <tr key={m.user_id} className="border-b border-border/60" data-testid={`team-row-${m.admin_role}`}>
                <td className="p-3 font-medium">{m.full_name || "—"}</td>
                <td className="p-3 font-mono text-xs text-primary" data-testid={`team-adminid-${m.user_id}`}>{m.admin_id || "—"}</td>
                <td className="p-3 text-muted-foreground">{m.email}</td>
                <td className="p-3">
                  {m.admin_role === "ceo" ? <span className="font-semibold">{m.role_label}</span> : (
                    <select disabled={busy} value={m.admin_role} onChange={(e) => act(() => api.put(`/admin/team/${m.user_id}/role`, { admin_role: e.target.value }), "Role updated")} className={sel}>
                      {roles.filter((r) => r.id !== "ceo").map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                    </select>
                  )}
                </td>
                <td className="p-3"><span className="inline-flex items-center gap-1 text-[10px] font-bold text-white px-2 py-0.5 rounded-full" style={{ background: "#2563EB" }}><ShieldCheck size={10} /> ADMIN</span></td>
                <td className="p-3">
                  <span className={m.pin_set ? "text-success" : "text-warning"}>{m.pin_reset_required ? "Reset required" : m.pin_set ? (m.pin_enabled ? "Set" : "Disabled") : "Not set"}</span>
                </td>
                <td className="p-3"><StatusBadge status={m.account_status} /></td>
                <td className="p-3">
                  {m.admin_role === "ceo" ? <span className="text-xs text-muted-foreground">—</span> : (
                    <div className="flex items-center gap-2">
                      <button title="Reset PIN" data-testid={`reset-pin-${m.user_id}`} onClick={() => act(() => api.post(`/admin/team/${m.user_id}/pin-reset`), "PIN reset — link emailed")} className="p-1.5 rounded-lg bg-secondary"><KeyRound size={14} /></button>
                      <button title={m.pin_enabled ? "Disable PIN" : "Enable PIN"} onClick={() => act(() => api.put(`/admin/team/${m.user_id}/pin-status`, { enabled: !m.pin_enabled }), "PIN access updated")} className="p-1.5 rounded-lg bg-secondary">{m.pin_enabled ? <Lock size={14} /> : <ShieldCheck size={14} />}</button>
                      <button title={m.account_status === "active" ? "Suspend" : "Reactivate"} data-testid={`status-${m.user_id}`} onClick={() => act(() => api.put(`/admin/team/${m.user_id}/status`, { status: m.account_status === "active" ? "suspended" : "active" }), "Status updated")} className="p-1.5 rounded-lg bg-secondary"><Ban size={14} className={m.account_status === "active" ? "text-warning" : "text-success"} /></button>
                      <button title="Remove" data-testid={`remove-${m.user_id}`} onClick={() => { if (window.confirm(`Remove ${m.email} from the admin team?`)) act(() => api.delete(`/admin/team/${m.user_id}`), "Member removed"); }} className="p-1.5 rounded-lg bg-destructive/10"><Trash2 size={14} className="text-destructive" /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InboxTab({ access }) {
  const TABS2 = ["unassigned", "waiting", "in_progress", "escalated", "resolved", "mine", "all"];
  const [tab, setTab] = useState("unassigned");
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);
  const [body, setBody] = useState("");
  const can = (p) => access.permissions.includes("*") || access.permissions.includes(p);

  const load = async () => {
    try { const { data } = await api.get(`/admin/support/inbox?tab=${tab}`); setList(data.conversations); }
    catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, [tab]);
  const openConv = async (id) => {
    try { const { data } = await api.get(`/admin/support/${id}`); setOpen(id); setDetail(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); await openConv(open); await load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="grid md:grid-cols-[320px_1fr] gap-4" data-testid="inbox-tab">
      <div>
        <div className="flex gap-1 overflow-x-auto no-scrollbar mb-3">
          {TABS2.map((t) => (
            <button key={t} data-testid={`inbox-tab-${t}`} onClick={() => setTab(t)}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap ${tab === t ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>{t.replace("_", " ")}</button>
          ))}
        </div>
        <div className="space-y-2 max-h-[70vh] overflow-y-auto">
          {list.length === 0 && <p className="text-sm text-muted-foreground p-3">No conversations.</p>}
          {list.map((c) => (
            <button key={c.conv_id} data-testid={`conv-${c.conv_id}`} onClick={() => openConv(c.conv_id)}
              className={`w-full text-left p-3 rounded-xl border ${open === c.conv_id ? "border-primary bg-primary/5" : "border-border bg-card"}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">{c.customer_label}</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${c.priority === "high" ? "bg-destructive/10 text-destructive" : "bg-secondary text-muted-foreground"}`}>{c.priority}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">{c.source === "web" ? "Website AI" : "App AI"} · {c.category} · {c.status}</div>
              <div className="text-xs text-foreground/70 truncate mt-1">{c.last_message}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-4 min-h-[300px]">
        {!detail ? <p className="text-sm text-muted-foreground">Select a conversation.</p> : (
          <div data-testid="conv-detail">
            <div className="flex items-center justify-between flex-wrap gap-2 pb-3 border-b border-border">
              <div>
                <div className="font-semibold">{detail.conversation.customer_label}</div>
                <div className="text-xs text-muted-foreground">{detail.conversation.source === "web" ? "Website AI" : "App AI"} · {detail.conversation.authenticated ? "authenticated" : "visitor"} · {detail.conversation.language} · {detail.conversation.category}</div>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {can("conversations.assign") && <button data-testid="conv-assign" onClick={() => act(() => api.post(`/admin/support/${open}/assign`, {}), "Assigned to you")} className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-secondary">Assign me</button>}
                {can("conversations.escalate") && <button data-testid="conv-escalate" onClick={() => act(() => api.post(`/admin/support/${open}/escalate`), "Escalated")} className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-warning/10 text-warning">Escalate</button>}
                {can("conversations.close") && <button data-testid="conv-resolve" onClick={() => act(() => api.post(`/admin/support/${open}/resolve`), "Resolved")} className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-success/10 text-success">Resolve</button>}
              </div>
            </div>
            {detail.conversation.ai_summary && <div className="mt-3 text-xs bg-secondary/60 rounded-xl p-3"><b>AI summary:</b> {detail.conversation.ai_summary}</div>}
            <div className="space-y-2 my-3 max-h-[42vh] overflow-y-auto">
              {detail.messages.map((m) => (
                <div key={m.msg_id} className={`text-sm p-2.5 rounded-xl ${m.internal ? "bg-warning/10 border border-warning/30" : m.sender === "agent" ? "bg-primary/10 ml-8" : m.sender === "ai" ? "bg-secondary/50" : m.sender === "system" ? "bg-muted text-xs italic" : "bg-secondary mr-8"}`}>
                  <div className="text-[10px] font-bold uppercase text-muted-foreground">{m.internal ? "Internal note" : m.sender}</div>
                  {m.body}
                </div>
              ))}
            </div>
            {can("conversations.reply") && (
              <div className="flex gap-2">
                <input data-testid="conv-reply-input" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Reply to customer…" className="flex-1 h-11 px-3 rounded-xl bg-secondary outline-none text-sm" />
                <button data-testid="conv-reply-send" onClick={() => body && act(() => api.post(`/admin/support/${open}/reply`, { body }).then(() => setBody("")), "Sent")} className="h-11 px-4 rounded-xl bg-primary text-white font-semibold"><SendIcon size={16} /></button>
                <button data-testid="conv-note-send" onClick={() => body && act(() => api.post(`/admin/support/${open}/note`, { body }).then(() => setBody("")), "Note added")} className="h-11 px-3 rounded-xl bg-secondary text-xs font-semibold">Note</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function GuardTab({ canEdit }) {
  const [sub, setSub] = useState("appeals");
  const [appeals, setAppeals] = useState(null);
  const [restricted, setRestricted] = useState(null);
  const [viewer, setViewer] = useState(null);

  const loadAppeals = async () => { try { const { data } = await api.get("/admin/appeals?status=pending"); setAppeals(data.appeals); } catch (e) { toast.error(errMsg(e)); } };
  const loadRestricted = async () => { try { const { data } = await api.get("/admin/restrictions"); setRestricted(data.users); } catch (e) { toast.error(errMsg(e)); } };
  useEffect(() => { loadAppeals(); loadRestricted(); }, []);

  const review = async (id, decision) => {
    const note = decision === "rejected" ? (window.prompt("Optional note to the user (why it was declined):") || "") : "";
    try { await api.post(`/admin/appeals/${id}/review`, { decision, note }); toast.success(decision === "approved" ? "Appeal approved — restrictions cleared" : "Appeal declined"); await loadAppeals(); await loadRestricted(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const lift = async (uid) => {
    try { const { data } = await api.post(`/admin/users/${uid}/lift-restriction`); toast.success(`Restriction lifted (${data.restriction_count}/5)`); await loadRestricted(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-3xl" data-testid="guard-tab">
      <div className="flex gap-2 mb-5">
        <button data-testid="guard-subtab-appeals" onClick={() => setSub("appeals")} className={`px-4 py-2 rounded-xl text-sm font-semibold ${sub === "appeals" ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>Pending Appeals {appeals ? `(${appeals.length})` : ""}</button>
        <button data-testid="guard-subtab-restricted" onClick={() => setSub("restricted")} className={`px-4 py-2 rounded-xl text-sm font-semibold ${sub === "restricted" ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>Restricted Users {restricted ? `(${restricted.length})` : ""}</button>
      </div>

      {sub === "appeals" && (appeals === null ? <FullLoader /> : (
        <div className="space-y-3">
          {appeals.length === 0 && <p className="text-sm text-muted-foreground">No pending appeals.</p>}
          {appeals.map((a) => (
            <div key={a.appeal_id} className="rounded-2xl border border-border bg-card p-4" data-testid={`appeal-${a.appeal_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-semibold flex items-center gap-2">{a.user?.display_name || a.user_id}
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600">{a.user?.account_status}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">{a.user?.email} · {a.user?.cash_tag} · {a.user?.restriction_count || 0}/5</div>
                </div>
                {canEdit && (
                  <div className="flex gap-2 shrink-0">
                    <button data-testid={`appeal-approve-${a.appeal_id}`} onClick={() => review(a.appeal_id, "approved")} className="text-sm font-semibold px-3 py-2 rounded-xl bg-success/10 text-success">Approve</button>
                    <button data-testid={`appeal-reject-${a.appeal_id}`} onClick={() => review(a.appeal_id, "rejected")} className="text-sm font-semibold px-3 py-2 rounded-xl bg-destructive/10 text-destructive">Decline</button>
                  </div>
                )}
              </div>
              <p className="text-sm mt-3 whitespace-pre-wrap">{a.explanation}</p>
              {a.photo_urls?.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {a.photo_urls.map((u, i) => (
                    <button key={i} onClick={() => setViewer(u)} data-testid={`appeal-photo-thumb-${a.appeal_id}-${i}`}
                      className="aspect-square rounded-xl overflow-hidden bg-secondary">
                      <AuthedImg src={u} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}

      {sub === "restricted" && (restricted === null ? <FullLoader /> : (
        <div className="rounded-2xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-muted-foreground"><tr>
              {["User", "Status", "Restrictions", "Appeal", "Action"].map((h) => <th key={h} className="text-left font-semibold px-4 py-3">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-border">
              {restricted.length === 0 && <tr><td colSpan={5} className="px-4 py-4 text-muted-foreground">No restricted users.</td></tr>}
              {restricted.map((u) => (
                <tr key={u.user_id} data-testid={`restricted-${u.user_id}`}>
                  <td className="px-4 py-3"><div className="font-medium">{u.display_name}</div><div className="text-xs text-muted-foreground">{u.email}</div></td>
                  <td className="px-4 py-3"><span className={`capitalize font-semibold ${u.account_status === "banned" ? "text-destructive" : ""}`}>{u.account_status}</span></td>
                  <td className="px-4 py-3 font-semibold">{u.restriction_count}/{u.max_restrictions}</td>
                  <td className="px-4 py-3">{u.has_pending_appeal ? <span className="text-amber-600 text-xs font-semibold">Pending</span> : <span className="text-muted-foreground text-xs">—</span>}</td>
                  <td className="px-4 py-3">
                    {canEdit && u.restriction_count > 0 && (
                      <button data-testid={`lift-${u.user_id}`} onClick={() => lift(u.user_id)} className="text-success text-xs font-semibold">Lift one</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {viewer && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 p-4" onClick={() => setViewer(null)} data-testid="appeal-photo-viewer">
          <AuthedImg src={viewer} className="max-h-[90vh] max-w-full rounded-2xl" />
        </div>
      )}
    </div>
  );
}

function ApprovalsTab() {
  const [reqs, setReqs] = useState([]);
  const load = async () => { try { const { data } = await api.get("/admin/approvals?status=pending"); setReqs(data.requests); } catch (e) { toast.error(errMsg(e)); } };
  useEffect(() => { load(); }, []);
  const act = async (id, path, msg) => { try { const { data } = await api.post(`/admin/approvals/${id}/${path}`); toast.success(data.status === "executed" ? "Approved & executed" : msg); await load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-3 max-w-2xl" data-testid="approvals-tab">
      <p className="text-sm text-muted-foreground">High-risk actions require two different admins to approve.</p>
      {reqs.length === 0 && <p className="text-sm text-muted-foreground">No pending approvals.</p>}
      {reqs.map((r) => (
        <div key={r.request_id} className="rounded-2xl border border-border bg-card p-4" data-testid={`approval-${r.request_id}`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold capitalize">{r.type.replace("_", " ")} · ${(r.amount_cents / 100).toFixed(2)}</div>
              <div className="text-xs text-muted-foreground">{r.reason} · approvals {r.approvals.length}/{r.required}</div>
            </div>
            <div className="flex gap-2">
              <button data-testid={`approve-${r.request_id}`} onClick={() => act(r.request_id, "approve", "Approval recorded")} className="text-sm font-semibold px-3 py-2 rounded-xl bg-success/10 text-success">Approve</button>
              <button data-testid={`reject-${r.request_id}`} onClick={() => act(r.request_id, "reject", "Rejected")} className="text-sm font-semibold px-3 py-2 rounded-xl bg-destructive/10 text-destructive">Reject</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MaintenanceTab() {
  const [state, setState] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/admin/maintenance"); setState(data); setMsg(data.message || ""); }
    catch (e) { toast.error(errMsg(e)); }
  })(); }, []);

  const apply = async (enabled) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/maintenance", { enabled, message: msg });
      setState(data); setMsg(data.message || "");
      toast.success(enabled ? "Maintenance mode is ON — customers now see the poster" : "Maintenance mode is OFF — site is live");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (!state) return <FullLoader />;

  return (
    <div className="max-w-xl" data-testid="maintenance-tab">
      <div className={`rounded-2xl border p-5 mb-4 ${state.enabled ? "bg-amber-500/10 border-amber-500/30" : "bg-card border-border"}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display font-semibold flex items-center gap-2"><Power size={18} className={state.enabled ? "text-amber-600" : "text-muted-foreground"} /> Maintenance mode</div>
            <div className="text-sm text-muted-foreground mt-1">
              Status: <span className={`font-semibold ${state.enabled ? "text-amber-600" : "text-success"}`}>{state.enabled ? "ON — customers see the poster" : "OFF — site is live"}</span>
            </div>
          </div>
          <span data-testid="maintenance-status" className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${state.enabled ? "bg-amber-500 text-white" : "bg-secondary text-muted-foreground"}`}>{state.enabled ? "Active" : "Off"}</span>
        </div>
        <p className="text-xs text-muted-foreground mt-3">When ON, customers see a full-screen Jack Cash maintenance poster. CEO and staff still have full access and can sign in to turn it off. Stripe and backend keep running.</p>
      </div>

      <label className="text-sm font-medium">Message shown to customers</label>
      <textarea data-testid="maintenance-message" value={msg} onChange={(e) => setMsg(e.target.value)} rows={3}
        className="w-full mt-1 mb-4 p-3 rounded-xl bg-secondary outline-none text-sm resize-none"
        placeholder="We'll be back online shortly — thank you for your patience." />

      <div className="flex gap-2">
        {state.enabled ? (
          <button data-testid="maintenance-disable-btn" onClick={() => apply(false)} disabled={busy}
            className="flex-1 h-12 rounded-xl bg-success text-white font-semibold flex items-center justify-center">
            {busy ? <Spinner className="w-5 h-5" /> : "Turn OFF — go live"}
          </button>
        ) : (
          <button data-testid="maintenance-enable-btn" onClick={() => apply(true)} disabled={busy}
            className="flex-1 h-12 rounded-xl bg-amber-500 text-white font-semibold flex items-center justify-center">
            {busy ? <Spinner className="w-5 h-5" /> : "Turn ON maintenance"}
          </button>
        )}
        <button data-testid="maintenance-save-msg-btn" onClick={() => apply(state.enabled)} disabled={busy}
          className="h-12 px-4 rounded-xl bg-secondary font-semibold text-sm">Save message</button>
      </div>
    </div>
  );
}


const SOCIAL_FIELDS = [
  { key: "tiktok", label: "TikTok", placeholder: "https://www.tiktok.com/@jackcash" },
  { key: "instagram", label: "Instagram", placeholder: "https://www.instagram.com/jackcash" },
  { key: "x", label: "X (Twitter)", placeholder: "https://x.com/jackcash" },
  { key: "facebook", label: "Facebook", placeholder: "https://www.facebook.com/jackcash" },
];

const STORE_FIELDS = [
  { key: "appstore", label: "Apple App Store", placeholder: "https://apps.apple.com/app/..." },
  { key: "playstore", label: "Google Play Store", placeholder: "https://play.google.com/store/apps/details?id=..." },
];

function SocialTab() {
  const [links, setLinks] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/admin/social"); setLinks(data); }
    catch (e) { toast.error(errMsg(e)); }
  })(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/social", links);
      setLinks(data);
      toast.success("Social links saved — now live on the public site");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (!links) return <FullLoader />;

  return (
    <div className="max-w-xl" data-testid="social-tab">
      <div className="rounded-2xl border border-border bg-card p-5 mb-4">
        <div className="font-display font-semibold flex items-center gap-2"><Share2 size={18} className="text-primary" /> Social media & app links</div>
        <p className="text-xs text-muted-foreground mt-2">Paste the full URL for each. Saved links appear publicly in the website footer, the "Get the app" section and the site's Organization data that Google reads. Leave a field empty to hide it (app badges show "SOON").</p>
      </div>

      <div className="space-y-4">
        {SOCIAL_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-sm font-medium">{f.label}</label>
            <input data-testid={`social-${f.key}-input`} value={links[f.key] || ""}
              onChange={(e) => setLinks({ ...links, [f.key]: e.target.value })}
              className="w-full mt-1 h-11 px-3 rounded-xl bg-secondary outline-none text-sm"
              placeholder={f.placeholder} />
          </div>
        ))}
        <div className="pt-2 border-t border-border">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mt-2 mb-1">Mobile app stores</div>
        </div>
        {STORE_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-sm font-medium">{f.label}</label>
            <input data-testid={`social-${f.key}-input`} value={links[f.key] || ""}
              onChange={(e) => setLinks({ ...links, [f.key]: e.target.value })}
              className="w-full mt-1 h-11 px-3 rounded-xl bg-secondary outline-none text-sm"
              placeholder={f.placeholder} />
          </div>
        ))}
      </div>

      <button data-testid="social-save-btn" onClick={save} disabled={busy}
        className="w-full h-12 mt-5 rounded-xl bg-primary text-white font-semibold flex items-center justify-center">
        {busy ? <Spinner className="w-5 h-5" /> : "Save & publish"}
      </button>
    </div>
  );
}


function FeesTab() {
  const [fees, setFees] = useState(null);
  const [pct, setPct] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { (async () => { const { data } = await api.get("/admin/settings/fees"); setFees(data); setPct(String(data.deposit_fee_percent)); })(); }, []);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/settings/fees", { deposit_fee_percent: parseFloat(pct) || 0 }); setFees({ ...fees, deposit_fee_percent: data.deposit_fee_percent }); toast.success("Fee updated"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  if (!fees) return <FullLoader />;
  return (
    <div className="max-w-lg space-y-4">
      <div className="rounded-2xl bg-card border border-border p-5">
        <div className="text-sm text-muted-foreground">Total fees earned (deposits)</div>
        <div data-testid="fees-earned" className="amount-figure text-4xl mt-1">{fees.total_earned_display}</div>
      </div>
      <div className="rounded-2xl bg-card border border-border p-5">
        <label className="font-display font-semibold">Deposit fee percentage</label>
        <p className="text-xs text-muted-foreground mb-3">Your commission taken from each card deposit (0–20%). The rest is credited to the user's wallet. Stripe's own processing fee is separate.</p>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input data-testid="fee-percent-input" type="number" step="0.1" min="0" max="20" value={pct} onChange={(e) => setPct(e.target.value)} className="w-full h-12 pr-8 pl-3 rounded-xl bg-secondary outline-none" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
          </div>
          <button data-testid="fee-save-btn" onClick={save} disabled={busy} className="px-6 h-12 rounded-xl bg-primary text-primary-foreground font-semibold">{busy ? "…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function HealthTab() {
  const [health] = useFetch("/health");
  if (!health) return <FullLoader />;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {Object.entries(health.checks).map(([name, c]) => (
        <div key={name} data-testid={`health-${name}`} className="rounded-2xl bg-card border border-border p-5 flex items-center justify-between">
          <span className="font-medium">{name}</span>
          <StatusBadge label={c.label} />
        </div>
      ))}
    </div>
  );
}

function ListTab({ endpoint, cols, testid }) {
  const [rows] = useFetch(endpoint);
  if (!rows) return <FullLoader />;
  if (rows.length === 0) return <p className="text-muted-foreground text-sm py-8 text-center">No records.</p>;
  return (
    <div className="rounded-2xl border border-border overflow-x-auto">
      <table className="w-full text-sm" data-testid={`${testid}-table`}>
        <thead className="bg-secondary text-muted-foreground"><tr>
          {cols.map((c) => <th key={c} className="text-left font-semibold px-4 py-3 capitalize">{c.replace("_", " ")}</th>)}
        </tr></thead>
        <tbody className="divide-y divide-border">
          {rows.map((r, i) => (
            <tr key={i}>
              {cols.map((c) => <td key={c} className="px-4 py-3 text-xs">{c.includes("created") ? new Date(r[c]).toLocaleString() : String(r[c] ?? "—")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BadgeTab() {
  const [rows, setRows] = useState(null);
  const load = async () => { try { const { data } = await api.get("/admin/badge/applications"); setRows(data); } catch (e) { toast.error(errMsg(e)); } };
  useEffect(() => { load(); }, []);

  const decide = async (userId, decision) => {
    try { await api.put(`/admin/badge/${userId}`, { decision }); toast.success(`Application ${decision}d`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const revoke = async (userId) => {
    if (!window.confirm("Remove this user's verified badge?")) return;
    try { await api.post(`/admin/badge/${userId}/revoke`); toast.success("Badge revoked"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!rows) return <FullLoader />;
  if (rows.length === 0) return <p className="text-muted-foreground text-sm py-8 text-center" data-testid="badge-empty">No badge applications yet.</p>;

  const badge = { pending: "bg-warning/10 text-warning", trialing: "bg-success/10 text-success", active: "bg-[#2F80FF]/10 text-[#2F80FF]", rejected: "bg-destructive/10 text-destructive", revoked: "bg-secondary text-muted-foreground", expired: "bg-secondary text-muted-foreground", canceled: "bg-secondary text-muted-foreground" };

  return (
    <div className="space-y-3" data-testid="admin-badge-tab">
      {rows.map((s) => (
        <div key={s.user_id} data-testid={`badge-row-${s.user_id}`} className="rounded-2xl bg-card border border-border p-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold">{s.user?.display_name}</span>
              <span className="font-mono text-xs text-muted-foreground">{s.user?.cash_tag}</span>
              <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${badge[s.status] || "bg-secondary"}`}>{s.status}</span>
              {s.status === "trialing" && <span className="text-[10px] font-bold text-success">GREEN</span>}
              {s.status === "active" && <span className="text-[10px] font-bold text-[#2F80FF]">BLUE</span>}
            </div>
            <div className="text-xs text-muted-foreground mt-1">{s.user?.email} · {s.plan_label} · {s.price_display} · {s.auto_renew ? "auto-renew on" : "auto-renew off"}</div>
          </div>
          <div className="flex gap-2 shrink-0">
            {s.status === "pending" && <>
              <button data-testid={`badge-approve-${s.user_id}`} onClick={() => decide(s.user_id, "approve")} className="text-xs font-semibold px-3 py-2 rounded-lg bg-success/10 text-success">Approve</button>
              <button data-testid={`badge-reject-${s.user_id}`} onClick={() => decide(s.user_id, "reject")} className="text-xs font-semibold px-3 py-2 rounded-lg bg-destructive/10 text-destructive">Reject</button>
            </>}
            {(s.status === "trialing" || s.status === "active") &&
              <button data-testid={`badge-revoke-${s.user_id}`} onClick={() => revoke(s.user_id)} className="text-xs font-semibold px-3 py-2 rounded-lg bg-destructive/10 text-destructive">Revoke</button>}
          </div>
        </div>
      ))}
    </div>
  );
}

function KycTab() {
  const [rows, setRows] = useState(null);
  const load = async () => { try { const { data } = await api.get("/admin/kyc"); setRows(data); } catch (e) { toast.error(errMsg(e)); } };
  useEffect(() => { load(); }, []);

  const decide = async (userId, decision) => {
    let note = null;
    if (decision === "rejected") { note = window.prompt("Reason for rejection (optional)") || ""; }
    try { await api.put(`/admin/kyc/${userId}`, { decision, note }); toast.success(`KYC ${decision}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!rows) return <FullLoader />;
  if (rows.length === 0) return <p className="text-muted-foreground text-sm py-8 text-center" data-testid="kyc-empty">No KYC submissions yet.</p>;

  const badge = { pending: "bg-warning/10 text-warning", verified: "bg-success/10 text-success", rejected: "bg-destructive/10 text-destructive" };

  return (
    <div className="space-y-3" data-testid="admin-kyc-tab">
      {rows.map((s) => (
        <div key={s.user_id} data-testid={`kyc-row-${s.user_id}`} className="rounded-2xl bg-card border border-border p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{s.legal_name}</span>
                <span className="font-mono text-xs text-muted-foreground">{s.user?.cash_tag}</span>
                <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${badge[s.status] || "bg-secondary"}`}>{s.status}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">{s.user?.email} · {s.phone} · {s.city}, {s.state} {s.zip_code} · DOB {s.dob}</div>
              {s.id_document_path && <a href={s.id_document_path} target="_blank" rel="noreferrer" className="text-xs font-semibold text-primary mt-1 inline-block">View ID document</a>}
            </div>
            <div className="flex gap-2 shrink-0">
              <button data-testid={`kyc-approve-${s.user_id}`} onClick={() => decide(s.user_id, "verified")} className="text-xs font-semibold px-3 py-2 rounded-lg bg-success/10 text-success">Approve</button>
              <button data-testid={`kyc-reject-${s.user_id}`} onClick={() => decide(s.user_id, "rejected")} className="text-xs font-semibold px-3 py-2 rounded-lg bg-destructive/10 text-destructive">Reject</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}


const SEV_CLS = {
  critical: "bg-destructive/10 text-destructive border-destructive/20",
  high: "bg-destructive/10 text-destructive border-destructive/20",
  medium: "bg-warning/10 text-warning border-warning/30",
  low: "bg-success/10 text-success border-success/20",
};

function SevBadge({ sev }) {
  return <span className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border ${SEV_CLS[sev] || SEV_CLS.low}`}>{sev}</span>;
}

function FraudTab() {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryBusy, setSummaryBusy] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/fraud/queue"); setData(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const runSummary = async () => {
    setSummaryBusy(true);
    try { const { data } = await api.post("/admin/fraud/summary"); setSummary(data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSummaryBusy(false); }
  };

  if (!data) return <FullLoader />;
  const isCeo = data.is_ceo;

  return (
    <div className="space-y-5" data-testid="fraud-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold flex items-center gap-2"><Fingerprint size={18} className="text-primary" /> Fraud detection</h2>
          <p className="text-sm text-muted-foreground">AI-assisted risk review. The AI advises only — it never moves money or acts on its own.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="fraud-refresh-btn" onClick={load} className="flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-xl bg-secondary"><RefreshCw size={15} /> Refresh</button>
          <button data-testid="fraud-summary-btn" onClick={runSummary} disabled={summaryBusy} className="flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-xl bg-primary text-primary-foreground disabled:opacity-60"><Sparkles size={15} /> {summaryBusy ? "Analyzing…" : "AI risk briefing"}</button>
        </div>
      </div>

      {!data.config_enabled && (
        <div className="rounded-2xl border border-warning/30 bg-warning/10 text-warning text-sm p-4 font-medium">The fraud AI is currently turned off by the CEO.</div>
      )}

      {summary && (
        <div data-testid="fraud-summary-card" className="rounded-2xl border border-primary/20 bg-primary/5 p-5">
          <div className="flex items-center gap-2 mb-2"><Sparkles size={16} className="text-primary" /><span className="font-display font-semibold">AI platform briefing</span><SevBadge sev={summary.assessment.severity} /></div>
          <p className="text-sm">{summary.assessment.summary}</p>
          {summary.assessment.reasons?.length > 0 && (
            <ul className="mt-3 flex flex-wrap gap-2">{summary.assessment.reasons.map((r, i) => <li key={i} className="text-xs bg-card border border-border rounded-full px-3 py-1">{r}</li>)}</ul>
          )}
          <p className="text-xs text-muted-foreground mt-3">Suggested next step: <span className="font-semibold capitalize">{summary.assessment.recommended_action}</span> · {summary.flagged_count} flagged account(s)</p>
        </div>
      )}

      <AiConfigPanel isCeo={isCeo} open={showConfig} onToggle={() => setShowConfig((v) => !v)} />

      <div>
        <h3 className="font-display font-semibold mb-3">Flagged queue ({data.cases.length})</h3>
        {data.cases.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="fraud-empty">
            <ShieldCheck size={28} className="mx-auto mb-2 text-success" />
            No suspicious activity detected right now.
          </div>
        ) : (
          <div className="space-y-3">
            {data.cases.map((c) => <FraudCase key={c.user_id} c={c} onChanged={load} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function FraudCase({ c, onChanged }) {
  const [ai, setAi] = useState(c.ai_assessment?.assessment || null);
  const [busy, setBusy] = useState(false);
  const [acting, setActing] = useState(false);

  const analyze = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/fraud/analyze/${c.user_id}`); setAi(data.assessment); toast.success("AI assessment ready"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    setActing(true);
    try { await api.put(`/admin/users/${c.user_id}/status`, { status }); toast.success(`Account ${status}`); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setActing(false); }
  };

  return (
    <div data-testid={`fraud-case-${c.user_id}`} className="rounded-2xl bg-card border border-border p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold">{c.display_name}</span>
            <span className="font-mono text-xs text-muted-foreground">{c.cash_tag}</span>
            <SevBadge sev={c.severity} />
            <span className="text-xs text-muted-foreground">score {c.heuristic_score}</span>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">{c.email} · balance {c.balance_display} · status {c.account_status}</div>
          <ul className="mt-2 flex flex-wrap gap-2">
            {c.signals.map((s, i) => <li key={i} className="text-xs bg-secondary rounded-full px-2.5 py-1">{s}</li>)}
          </ul>
        </div>
        <button data-testid={`fraud-analyze-${c.user_id}`} onClick={analyze} disabled={busy} className="shrink-0 flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl bg-primary text-primary-foreground disabled:opacity-60">
          <Sparkles size={14} /> {busy ? "Analyzing…" : ai ? "Re-analyze" : "AI analyze"}
        </button>
      </div>

      {ai && (
        <div data-testid={`fraud-ai-${c.user_id}`} className="mt-3 rounded-xl border border-primary/20 bg-primary/5 p-3">
          <div className="flex items-center gap-2 mb-1"><Sparkles size={14} className="text-primary" /><span className="text-sm font-semibold">AI assessment</span><SevBadge sev={ai.severity} /><span className="text-xs text-muted-foreground">risk {ai.risk_score}/100</span></div>
          <p className="text-sm">{ai.summary}</p>
          {ai.reasons?.length > 0 && <ul className="mt-2 list-disc pl-5 text-xs text-muted-foreground space-y-0.5">{ai.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground">Recommends: <span className="font-semibold capitalize">{ai.recommended_action}</span></span>
            <button data-testid={`fraud-freeze-${c.user_id}`} onClick={() => setStatus("frozen")} disabled={acting} className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-warning/10 text-warning disabled:opacity-60"><Snowflake size={13} /> Freeze</button>
            <button data-testid={`fraud-suspend-${c.user_id}`} onClick={() => setStatus("suspended")} disabled={acting} className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-destructive/10 text-destructive disabled:opacity-60"><Ban size={13} /> Suspend</button>
            <button data-testid={`fraud-clear-${c.user_id}`} onClick={() => setStatus("active")} disabled={acting} className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-secondary">Mark active</button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">You are taking this action, not the AI. The AI only advises.</p>
        </div>
      )}
    </div>
  );
}

function AiConfigPanel({ isCeo, open, onToggle }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open && !cfg) (async () => { try { const { data } = await api.get("/admin/fraud/ai-config"); setCfg(data.config); } catch (e) { toast.error(errMsg(e)); } })(); }, [open, cfg]);

  const field = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        velocity_count: parseInt(cfg.velocity_count) || 1,
        velocity_window_min: parseInt(cfg.velocity_window_min) || 1,
        new_account_hours: parseInt(cfg.new_account_hours) || 1,
        large_amount: Math.round(parseFloat(cfg.large_amount) || 100),
        failed_threshold: parseInt(cfg.failed_threshold) || 1,
        cashout_ratio: parseFloat(cfg.cashout_ratio) || 0.1,
        extra_instructions: cfg.extra_instructions || "",
        enabled: !!cfg.enabled,
      };
      const { data } = await api.put("/admin/fraud/ai-config", payload);
      setCfg(data.config); toast.success("Fraud AI settings saved");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl bg-card border border-border overflow-hidden">
      <button data-testid="fraud-config-toggle" onClick={onToggle} className="w-full flex items-center justify-between p-4 text-left">
        <span className="flex items-center gap-2 font-display font-semibold"><Lock size={16} className="text-primary" /> AI rules & limits</span>
        <span className="text-xs font-semibold text-muted-foreground">{isCeo ? "CEO only" : "View only"}</span>
      </button>
      {open && (
        <div className="border-t border-border p-4">
          {!isCeo && <div className="mb-3 text-xs text-warning bg-warning/10 border border-warning/30 rounded-lg px-3 py-2 font-medium">Only the CEO can change these settings.</div>}
          {!cfg ? <FullLoader /> : (
            <fieldset disabled={!isCeo} className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <CfgNum label="Velocity sends" k="velocity_count" cfg={cfg} field={field} />
                <CfgNum label="Window (min)" k="velocity_window_min" cfg={cfg} field={field} />
                <CfgNum label="New acct (hrs)" k="new_account_hours" cfg={cfg} field={field} />
                <CfgNum label="Large amount (¢)" k="large_amount" cfg={cfg} field={field} />
                <CfgNum label="Failed threshold" k="failed_threshold" cfg={cfg} field={field} />
                <CfgNum label="Cash-out ratio" k="cashout_ratio" step="0.05" cfg={cfg} field={field} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Extra guidance for the AI</label>
                <textarea data-testid="fraud-cfg-instructions" value={cfg.extra_instructions} onChange={(e) => field("extra_instructions", e.target.value)} rows={3} placeholder="e.g. Pay extra attention to accounts sending to many brand-new recipients." className="w-full mt-1 p-3 rounded-xl bg-secondary outline-none text-sm disabled:opacity-60" />
              </div>
              <label className="flex items-center gap-2 text-sm font-medium">
                <input data-testid="fraud-cfg-enabled" type="checkbox" checked={!!cfg.enabled} onChange={(e) => field("enabled", e.target.checked)} className="w-4 h-4 accent-[color:hsl(var(--primary))]" />
                Fraud AI enabled
              </label>
              {isCeo && <button data-testid="fraud-cfg-save" onClick={save} disabled={busy} className="h-11 px-6 rounded-xl bg-primary text-primary-foreground font-semibold disabled:opacity-60">{busy ? "Saving…" : "Save rules"}</button>}
            </fieldset>
          )}
        </div>
      )}
    </div>
  );
}

function CfgNum({ label, k, cfg, field, step }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground">{label}</label>
      <input data-testid={`fraud-cfg-${k}`} type="number" step={step || "1"} value={cfg[k]} onChange={(e) => field(k, e.target.value)} className="w-full h-11 px-3 mt-1 rounded-xl bg-secondary outline-none disabled:opacity-60" />
    </div>
  );
}

function ResolutionTab({ access }) {
  const can = (p) => access.permissions.includes("*") || access.permissions.includes(p);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [cust, setCust] = useState(null);
  const [cats, setCats] = useState([]);
  const [category, setCategory] = useState("");
  const [desc, setDesc] = useState("");
  const [priority, setPriority] = useState("normal");
  const [actions, setActions] = useState([]);
  const [busy, setBusy] = useState("");
  const [outcome, setOutcome] = useState(null);

  const doSearch = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 2) { toast.error("Type at least 2 characters"); return; }
    setSearching(true);
    try { const r = await api.get(`/admin/resolution/search?q=${encodeURIComponent(q.trim())}`); setResults(r.data.results); }
    catch (err) { toast.error(errMsg(err)); } finally { setSearching(false); }
  };

  const selectCustomer = async (u) => {
    setOutcome(null); setCategory(""); setActions([]); setDesc("");
    try {
      const r = await api.get(`/admin/resolution/customer/${u.user_id}`);
      setCust(r.data.customer); setCats(r.data.categories);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const pickCategory = async (c) => {
    setCategory(c); setOutcome(null);
    if (!c) { setActions([]); return; }
    try { const r = await api.get(`/admin/resolution/actions?category=${c}`); setActions(r.data.actions); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const runAction = async (actionId, forceEscalate = false) => {
    setBusy(actionId);
    try {
      const r = await api.post("/admin/resolution/action", {
        user_id: cust.user_id, category: forceEscalate ? category : category,
        action: forceEscalate ? "escalate" : actionId, description: desc, priority,
      });
      setOutcome({ ok: true, ...r.data });
      toast.success(r.data.result?.message || "Action completed");
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.code === "NO_PERMISSION") {
        setOutcome({ ok: false, noPerm: true, message: d.message });
      } else { toast.error(errMsg(err)); }
    } finally { setBusy(""); }
  };

  const reset = () => { setCust(null); setResults([]); setQ(""); setCategory(""); setActions([]); setOutcome(null); setDesc(""); };

  return (
    <div className="max-w-3xl space-y-5" data-testid="resolution-tab">
      <div className="rounded-2xl bg-card border border-border p-5">
        <div className="flex items-center gap-2 mb-1"><LifeBuoy size={18} className="text-primary" />
          <h2 className="font-display text-xl font-semibold">Quick Customer Resolution</h2></div>
        <p className="text-sm text-muted-foreground mb-4">Search a customer, verify them, then take only the actions your role allows. No passwords, PINs or secrets are ever shown.</p>

        {!cust && (
          <form onSubmit={doSearch} className="flex gap-2">
            <div className="flex-1 flex items-center gap-2 h-11 px-3 rounded-xl bg-secondary">
              <Search size={16} className="text-muted-foreground" />
              <input data-testid="resolution-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Name, email, phone, DOB (YYYY-MM-DD), Customer ID, or $cashtag" className="flex-1 bg-transparent outline-none text-sm" />
            </div>
            <button data-testid="resolution-search-btn" type="submit" disabled={searching}
              className="px-5 h-11 rounded-xl bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-60">
              {searching ? <Spinner /> : "Search"}</button>
          </form>
        )}

        {!cust && results.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="resolution-results">
            {results.map((u) => (
              <button key={u.user_id} data-testid={`resolution-result-${u.user_id}`} onClick={() => selectCustomer(u)}
                className="w-full flex items-center justify-between gap-3 p-3 rounded-xl bg-secondary hover:bg-secondary/70 text-left">
                <div>
                  <div className="font-medium text-sm">{u.display_name || u.full_name || "—"} <span className="text-muted-foreground">{u.cash_tag}</span></div>
                  <div className="text-xs text-muted-foreground">{u.email} · {u.phone || "no phone"}</div>
                </div>
                <StatusBadge status={u.account_status} />
              </button>
            ))}
          </div>
        )}
      </div>

      {cust && (
        <div className="rounded-2xl bg-card border border-border p-5" data-testid="resolution-customer">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><UserCheck size={18} className="text-primary" />
              <h3 className="font-semibold">Verified customer</h3></div>
            <button data-testid="resolution-change-customer" onClick={reset} className="text-xs font-semibold text-muted-foreground">Change</button>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info label="Name" value={cust.full_name || cust.display_name} />
            <Info label="Cashtag" value={cust.cash_tag} />
            <Info label="Email" value={cust.email} />
            <Info label="Phone" value={cust.phone || "—"} />
            <Info label="Date of birth" value={cust.dob || "—"} />
            <Info label="Country" value={cust.country || "—"} />
            <Info label="Customer ID" value={cust.user_id} />
            <Info label="Account" value={cust.account_status} />
            <Info label="Verification" value={cust.verification_status} />
            {cust.balance != null && <Info label="Balance" value={`${cust.balance} ${cust.currency || ""}`} />}
            <Info label="Open cases" value={String(cust.open_cases ?? 0)} />
          </div>

          <div className="mt-5">
            <label className="text-xs text-muted-foreground">What problem are you trying to fix?</label>
            <select data-testid="resolution-category-select" value={category} onChange={(e) => pickCategory(e.target.value)}
              className="w-full h-11 px-3 mt-1 rounded-xl bg-secondary outline-none text-sm">
              <option value="">Select a problem category…</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>

          {category && (
            <>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <label className="text-xs text-muted-foreground">Describe the issue</label>
                  <textarea data-testid="resolution-desc" value={desc} onChange={(e) => setDesc(e.target.value)} rows={2}
                    className="w-full px-3 py-2 mt-1 rounded-xl bg-secondary outline-none text-sm resize-none" placeholder="Short description…" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Priority</label>
                  <select data-testid="resolution-priority" value={priority} onChange={(e) => setPriority(e.target.value)}
                    className="w-full h-11 px-3 mt-1 rounded-xl bg-secondary outline-none text-sm">
                    <option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Authorized actions</div>
                {actions.map((a) => (
                  <button key={a.id} data-testid={`resolution-action-${a.id}`} disabled={!a.allowed || busy}
                    onClick={() => a.allowed && runAction(a.id)}
                    className={`w-full flex items-center justify-between gap-2 p-3 rounded-xl text-left text-sm font-medium transition-colors ${a.allowed ? "bg-secondary hover:bg-secondary/70" : "bg-secondary/40 opacity-60 cursor-not-allowed"}`}>
                    <span>{a.label}</span>
                    {busy === a.id ? <Spinner /> : a.allowed ? <CheckCircle2 size={16} className="text-primary" /> : <Lock size={14} className="text-muted-foreground" />}
                  </button>
                ))}
              </div>
            </>
          )}

          {outcome && (
            <div data-testid="resolution-outcome" className={`mt-4 p-4 rounded-xl border text-sm ${outcome.ok ? "border-primary/40 bg-primary/5" : "border-destructive/40 bg-destructive/5"}`}>
              {outcome.ok ? (
                <>
                  <div className="font-semibold flex items-center gap-2"><CheckCircle2 size={16} className="text-primary" /> {outcome.result?.message || "Done"}</div>
                  {outcome.result?.case_number && <div className="text-xs mt-1">Case: <span className="font-mono">{outcome.result.case_number}</span></div>}
                  <div className="text-xs mt-1 text-muted-foreground">Status: <StatusBadge status={outcome.status} /></div>
                  {outcome.result?.recent && <pre className="mt-2 text-xs bg-background p-2 rounded overflow-auto">{JSON.stringify(outcome.result.recent, null, 1)}</pre>}
                  {outcome.result?.cards && <pre className="mt-2 text-xs bg-background p-2 rounded overflow-auto">{JSON.stringify(outcome.result.cards, null, 1)}</pre>}
                </>
              ) : (
                <>
                  <div className="font-semibold flex items-center gap-2 text-destructive"><ShieldAlert size={16} /> {outcome.message}</div>
                  {can("conversations.escalate") && (
                    <button data-testid="resolution-escalate-btn" onClick={() => runAction("escalate", true)}
                      className="mt-3 px-4 h-10 rounded-xl bg-primary text-primary-foreground text-sm font-semibold">Escalate to an authorized team member</button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium break-all">{value || "—"}</div>
    </div>
  );
}
